import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, ViewChild, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Maybe } from '../../common/utils/utils';
import { FormHelper } from '../../common/helpers/form.helper';
import { extractSyntaxFromFileName } from '../../file-utils';
import {
  MonacoEditorFormControlComponent
} from '../../common/components/monaco-editor-form-control/monaco-editor-form-control.component';

export interface PatternFileEditorCreatePayload {
  mode: 'create';
  alreadyTakenFileNames: string[];
}
export interface PatternFileEditorEditPayload {
  mode: 'edit';
  content: string;
  fileName: string;
}

export type PatternFileEditorDialogPayload = PatternFileEditorCreatePayload | PatternFileEditorEditPayload;

/**
 * If the `fileName` is present, the file is new, if it's not present, and existing file is edited.
 */
export interface PatternFileEditorDialogResult {
  fileContentText: string;
  fileName?: string;
}

@Component({
    selector: 'adm4-pattern-file-editor-dialog',
    template: `
    <adm4-modal-dialog-title
      [header]='header'
      [showClose]='true'
      [isFullHeightContent]='true'
      (closeClicked)="closeDialog()">
      <form [formGroup]="form" class="full-height-flex" (submit)='save($event)'>
        <div class="content-container flex-grow-1 bg-screen-white d-flex flex-column">
          @if (!isEditMode) {
            <div class="adm4-form-group" [class.has-error]="shouldShowNameInvalidState()">
              <label for="fileName" class="input-label">File name*</label>
              <input type="text" id="fileName" class="form-control admn4-text-input" [formControlName]="nameControlName" cdkFocusInitial>
              <div class="validation-message-container">
                @if (shouldShowNameRequiredMessage()) {
                  <adm4-validation-message
                    [isError]='true'
                  message="The file name is required."></adm4-validation-message>
                }
                @if (shouldShowNameDupedMessage()) {
                  <adm4-validation-message
                    [isError]='true'
                  message="A file with that name already exists."></adm4-validation-message>
                }
              </div>
            </div>
          }
          <div class="adm4-form-group flex-grow-1 d-flex flex-column" [class.has-error]="shouldShowValueInvalidState()">
            <div class="d-flex justify-content-between align-items-start">
              <label for="fileContent" class="input-label">File content*</label>
              <button type="button" class="toggle-fullscreen admn4-button-text color-green-c1" (click)="toggleFullScreen()">
                <mat-icon class="size-20">{{isFullScreen ? 'fullscreen_exit' : 'fullscreen'}}</mat-icon>
                <span>{{isFullScreen ? 'Exit fullscreen' : 'Fullscreen'}}</span>
              </button>
            </div>
            <adm4-monaco-editor-form-control id="fileContent" class="form-control admn4-text-input flex-grow-1" cdkFocusInitial tabindex="0"
              [formControlName]="contentControlName" [syntax]="editorSyntax"
              (editorChanged)="monacoChanged()"
            ></adm4-monaco-editor-form-control>
            <div class="validation-message-container">
              @if (shouldShowContentRequiredMessage()) {
                <adm4-validation-message
                  [isError]='true'
                message="The file content can not be empty."></adm4-validation-message>
              }
            </div>
          </div>
          <adm4-admonition severity="info" alignment="center">
            <p adm4-admonition-msg class="m-0 color-info">The edited content will be saved with <code>utf-8</code> encoding.</p>
          </adm4-admonition>
        </div>
        <mat-dialog-actions>
          <button class='admn4-button-text' type='button' (click)='closeDialog()'>Cancel</button>
          <button class="admn4-button-ellipse-blue" type='button' [disabled]='!form.dirty || !form.valid' (click)="save($event)">Done</button>
        </mat-dialog-actions>
      </form>
    </adm4-modal-dialog-title>
    `,
    styleUrls: ['../../common/styles/component-specific/modal-window.scss'],
    host: { '[class]': "'adm4-override-mdc-dialog-component-host'" },
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class PatternFileEditorDialogComponent implements OnDestroy {
  private dialogRef = inject<MatDialogRef<PatternFileEditorDialogComponent, PatternFileEditorDialogResult | null>>(MatDialogRef);
  private cdRef = inject(ChangeDetectorRef);


  @ViewChild(MonacoEditorFormControlComponent, {static: true, read: ElementRef}) editorElement: ElementRef;

  private static readonly FILE_DUPE: string = 'fileNameAlreadyExists';

  private readonly destroyed$: Subject<boolean> = new Subject();

  public readonly contentControlName = 'fileContent';
  public readonly nameControlName = 'fileName';

  public readonly isEditMode: boolean;
  private readonly alreadyTakenFileNames: string[] = [];

  public readonly header: string;

  public readonly form: FormGroup<{
    fileContent: FormControl<string>;
    fileName: FormControl<Maybe<string>>;
  }>;

  public editorSyntax: string;

  public isFullScreen: boolean = false;

  constructor() {
    const fb = inject(FormBuilder);
    const payload = inject<PatternFileEditorDialogPayload>(MAT_DIALOG_DATA);

    if (payload.mode === 'edit') {
      this.form = fb.group({
        fileContent: new FormControl<string>(payload.content, {validators: [Validators.required], nonNullable: true}),
        fileName: new FormControl<Maybe<string>>({value: '', disabled: true}),
      });
      this.isEditMode = true;
      this.header = `Edit content of ${payload.fileName}`;
      this.editorSyntax = extractSyntaxFromFileName(payload.fileName);
    } else {
      this.isEditMode = false;
      this.header = 'Create new file';
      this.alreadyTakenFileNames = payload.alreadyTakenFileNames;
      const alreadyTakenValidator: ValidatorFn = (control: AbstractControl) => {
        const value = control.value;
        if (typeof value === 'string' && this.alreadyTakenFileNames.some((fileName: string) => fileName === value)) {
          return {[PatternFileEditorDialogComponent.FILE_DUPE]: true};
        }
        return null;
      };
      this.form = fb.group({
        fileContent: new FormControl<string>('', {validators: [Validators.required], nonNullable: true}),
        fileName: new FormControl<Maybe<string>>({value: '', disabled: false}, {validators: [Validators.required, alreadyTakenValidator], nonNullable: true}),
      });

      this.form.controls.fileName.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((fileName: Maybe<string>) => {
        this.editorSyntax = extractSyntaxFromFileName(fileName);
      });
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public monacoChanged(): void {
    this.cdRef.detectChanges();
  }

  public save(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.form.valid) {
      const newContent = this.form.controls.fileContent.value;
      if (this.isEditMode) {
        this.dialogRef.close({fileContentText: newContent});
      } else {
        const newFileName = this.form.controls.fileName.value || 'unnamed-file';
        this.dialogRef.close({fileContentText: newContent, fileName: newFileName});
      }
    }
  }

  public closeDialog() {
    this.dialogRef.close(null);
  }

  public shouldShowNameRequiredMessage(): boolean {
    return FormHelper.shouldShowFormControlErrorForKey(this.form.controls.fileName, FormHelper.VALIDATION_KEY_REQUIRED);
  }

  public shouldShowNameDupedMessage(): boolean {
    return FormHelper.shouldShowFormControlErrorForKey(this.form.controls.fileName, PatternFileEditorDialogComponent.FILE_DUPE);
  }

  public shouldShowContentRequiredMessage(): boolean {
    return FormHelper.shouldShowFormControlErrorForKey(this.form.controls.fileContent, FormHelper.VALIDATION_KEY_REQUIRED);
  }

  public shouldShowNameInvalidState(): boolean {
    return FormHelper.shouldShowFormControlInvalid(this.form.controls.fileName);
  }

  public shouldShowValueInvalidState(): boolean {
    return FormHelper.shouldShowFormControlInvalid(this.form.controls.fileContent);
  }

  public toggleFullScreen(): void {
    if (this.isFullScreen) {
      this.dialogRef.removePanelClass('dialog-full-screen');
    } else {
      this.dialogRef.addPanelClass('dialog-full-screen');
    }
    this.isFullScreen = !this.isFullScreen;
    setTimeout(() => this.editorElement?.nativeElement?.focus(), 0);
  }

}
